import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ListBox,
    processListBoxDragAndDrop,
    ListBoxDragEvent,
    processListBoxData,
} from '@progress/kendo-react-listbox';
import React, { ComponentType, useState } from 'react';
import Button from 'views/components/core/buttons';
import './styles.scss';
import { textToId } from '@utils/automation-test-helpers';

interface IDragDropListItemWithDataItem extends DragDropListItem {
    dataItem: DragDropListItem;
}

const ListItem: ComponentType<IDragDropListItemWithDataItem> = (props) => {
    const { dataItem, ...others } = props;
    const { text, index } = dataItem;
    return (
        <li {...others} data-testid={`list-item-${textToId(text, index)}`}>
            {text}
        </li>
    );
};

export interface DragDropListItem {
    key: number | string;
    text: string;
    index: number;
}

interface MultiListDragDropProps {
    list1Items: DragDropListItem[];
    list2Items: DragDropListItem[];
    updateParent: (list1Items: DragDropListItem[], list2Items: DragDropListItem[]) => void;
    list1Header: string;
    list2Header: string;
    reset?: () => void;
    error?: string;
}

const MultiListDragDrop = ({
    list1Items,
    list2Items,
    updateParent,
    list1Header,
    list2Header,
    reset,
    error,
}: MultiListDragDropProps) => {
    const [draggedItem, setDraggedItem] = useState<DragDropListItem | null>(null);

    const handleDragStart = (event: ListBoxDragEvent) => {
        setDraggedItem(event.dataItem);
    };

    const handleDrop = (event: ListBoxDragEvent) => {
        const result = processListBoxDragAndDrop(list1Items, list2Items, draggedItem, event.dataItem, 'key');
        updateParent(result.listBoxOneData, result.listBoxTwoData);
    };

    const moveAll = (toolName: string) => {
        const result = processListBoxData(list1Items, list2Items, toolName, 'key');

        updateParent(result.listBoxOneData, result.listBoxTwoData);
    };

    return (
        <div className="multi-list-drag-drop-wrapper">
            <div className="multi-list-drag-drop-content-wrapper">
                <div className="multi-list-drag-drop-element multi-list-drag-drop-list-box-wrapper">
                    <h3>{list1Header}</h3>
                    <ListBox
                        className="multi-list-drag-drop-list-box"
                        data={list1Items}
                        textField="text"
                        onDragStart={handleDragStart}
                        onDrop={handleDrop}
                        item={ListItem}
                    />
                </div>
                <div className="multi-list-drag-drop-element multi-list-drag-drop-toolbar">
                    <Button
                        className="multi-list-move-all-list-one"
                        disabled={list2Items.length === 0}
                        onClick={() => moveAll('transferAllFrom')}
                        data-testid="multi-list-move-all-list-one"
                    >
                        <FontAwesomeIcon icon={['fas', 'chevron-double-left']} className="fas-chevron-double-left" />
                    </Button>
                    <Button
                        className="multi-list-move-all-list-two"
                        disabled={list1Items.length === 0}
                        onClick={() => moveAll('transferAllTo')}
                        data-testid="multi-list-move-all-list-two"
                    >
                        <FontAwesomeIcon icon={['fas', 'chevron-double-right']} className="fas-chevron-double-right" />
                    </Button>
                    <Button className="multi-list-reset" onClick={reset} data-testid="multi-list-reset">
                        <FontAwesomeIcon icon={['fas', 'undo']} className="fas-undo" />
                    </Button>
                </div>
                <div className="multi-list-drag-drop-element multi-list-drag-drop-list-box-wrapper">
                    <h3>{list2Header}</h3>
                    <ListBox
                        className="multi-list-drag-drop-list-box"
                        data={list2Items}
                        textField="text"
                        onDragStart={handleDragStart}
                        onDrop={handleDrop}
                        item={ListItem}
                    />
                </div>
            </div>
            {error && <p className="error-message">{error}</p>}
        </div>
    );
};

export default MultiListDragDrop;
